/**
 * GuestNotesSection Component
 * Notes management for guest project view
 */
import React, { useState } from 'react';
import { Card, Button, Form } from 'react-bootstrap';
import { MessageSquare, Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import { ProjectNote } from '../../../types/projects';
import ConfirmationModal from '../../../Components/ConfirmationModal';

interface GuestNotesSectionProps {
    notes: { [key: string]: ProjectNote } | undefined;
    externalUserId: string;
    onAddNote: (title: string, content: string) => Promise<void>;
    onUpdateNote: (noteKey: string, title: string, content: string) => Promise<void>;
    onDeleteNote: (noteKey: string) => Promise<void>;
    submitting: boolean;
    canDelete: (authorId: string) => boolean;
    /** Completed project: everything is shown, nothing can be added or changed. */
    readOnly?: boolean;
}

const MAX_TITLE_LENGTH = 200;
const MAX_CONTENT_LENGTH = 5000;

const GuestNotesSection: React.FC<GuestNotesSectionProps> = ({
    notes,
    externalUserId,
    onAddNote,
    onUpdateNote,
    onDeleteNote,
    submitting,
    canDelete,
    readOnly = false,
}) => {
    const [showAddForm, setShowAddForm] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [newContent, setNewContent] = useState('');
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [editingTitle, setEditingTitle] = useState('');
    const [editingContent, setEditingContent] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; noteKey: string | null }>({
        show: false,
        noteKey: null,
    });

    const notesArray = notes
        ? Object.entries(notes).map(([key, note]) => ({ ...note, key }))
        : [];

    const handleAdd = async () => {
        if (!newTitle.trim() || !newContent.trim()) return;
        await onAddNote(newTitle.trim(), newContent.trim());
        setNewTitle('');
        setNewContent('');
        setShowAddForm(false);
    };

    const handleStartEdit = (note: ProjectNote & { key: string }) => {
        setEditingKey(note.key);
        setEditingTitle(note.title || '');
        setEditingContent(note.content || '');
    };

    const handleSaveEdit = async () => {
        if (!editingKey || !editingTitle.trim() || !editingContent.trim()) return;
        await onUpdateNote(editingKey, editingTitle.trim(), editingContent.trim());
        setEditingKey(null);
        setEditingTitle('');
        setEditingContent('');
    };

    const handleCancelEdit = () => {
        setEditingKey(null);
        setEditingTitle('');
        setEditingContent('');
    };

    const handleDeleteClick = (noteKey: string) => {
        setDeleteConfirm({ show: true, noteKey });
    };

    const handleDeleteConfirm = async () => {
        if (deleteConfirm.noteKey) {
            await onDeleteNote(deleteConfirm.noteKey);
        }
        setDeleteConfirm({ show: false, noteKey: null });
    };

    const handleDeleteCancel = () => {
        setDeleteConfirm({ show: false, noteKey: null });
    };

    return (
        <>
            <Card className="guest-notes-section mb-4">
                <Card.Header className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                        <MessageSquare size={18} />
                        <span>Anteckningar</span>
                        {notesArray.length > 0 && (
                            <span className="badge bg-secondary">{notesArray.length}</span>
                        )}
                    </div>
                    {!showAddForm && !readOnly && (
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => setShowAddForm(true)}
                            disabled={submitting}
                        >
                            <Plus size={16} className="me-1" />
                            Lägg till
                        </Button>
                    )}
                </Card.Header>
                <Card.Body>
                    {showAddForm && (
                        <div className="guest-notes-section__add-form mb-3 p-3 bg-light rounded">
                            <Form.Control
                                type="text"
                                placeholder="Titel"
                                value={newTitle}
                                onChange={(e) => setNewTitle(e.target.value)}
                                maxLength={MAX_TITLE_LENGTH}
                                disabled={submitting}
                                autoFocus
                                className="mb-2"
                            />
                            <small className={`d-block text-end mb-2 ${newTitle.length >= MAX_TITLE_LENGTH ? 'text-danger' : 'text-muted'}`}>
                                {newTitle.length}/{MAX_TITLE_LENGTH}
                            </small>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="Skriv din anteckning..."
                                value={newContent}
                                onChange={(e) => setNewContent(e.target.value)}
                                maxLength={MAX_CONTENT_LENGTH}
                                disabled={submitting}
                            />
                            <div className="d-flex justify-content-between align-items-center mt-2">
                                <small className="text-muted">
                                    {newContent.length}/{MAX_CONTENT_LENGTH}
                                </small>
                                <div className="d-flex gap-2">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => {
                                            setShowAddForm(false);
                                            setNewTitle('');
                                            setNewContent('');
                                        }}
                                        disabled={submitting}
                                    >
                                        Avbryt
                                    </Button>
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={handleAdd}
                                        disabled={!newTitle.trim() || !newContent.trim() || submitting}
                                    >
                                        {submitting ? 'Sparar...' : 'Spara'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {notesArray.length === 0 && !showAddForm && (
                        <p className="text-muted text-center mb-0">
                            Inga anteckningar ännu
                        </p>
                    )}

                    <div className="guest-notes-section__list">
                        {notesArray.map((note) => (
                            <div
                                key={note.key}
                                className="guest-notes-section__note p-3 border rounded mb-2"
                            >
                                {editingKey === note.key ? (
                                    <>
                                        <Form.Control
                                            type="text"
                                            value={editingTitle}
                                            onChange={(e) => setEditingTitle(e.target.value)}
                                            maxLength={MAX_TITLE_LENGTH}
                                            disabled={submitting}
                                            autoFocus
                                            className="mb-2"
                                        />
                                        <small className={`d-block text-end mb-2 ${editingTitle.length >= MAX_TITLE_LENGTH ? 'text-danger' : 'text-muted'}`}>
                                            {editingTitle.length}/{MAX_TITLE_LENGTH}
                                        </small>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={editingContent}
                                            onChange={(e) => setEditingContent(e.target.value)}
                                            maxLength={MAX_CONTENT_LENGTH}
                                            disabled={submitting}
                                        />
                                        <small className={`d-block text-end mt-1 ${editingContent.length >= MAX_CONTENT_LENGTH ? 'text-danger' : 'text-muted'}`}>
                                            {editingContent.length}/{MAX_CONTENT_LENGTH}
                                        </small>
                                        <div className="d-flex justify-content-end gap-2 mt-2">
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={handleCancelEdit}
                                                disabled={submitting}
                                            >
                                                <X size={14} className="me-1" />
                                                Avbryt
                                            </Button>
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={handleSaveEdit}
                                                disabled={!editingTitle.trim() || !editingContent.trim() || submitting}
                                            >
                                                <Save size={14} className="me-1" />
                                                {submitting ? 'Sparar...' : 'Spara'}
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {note.title && (
                                            <h6 className="mb-2">{note.title}</h6>
                                        )}
                                        <p className="mb-2 white-space-pre-wrap">{note.content}</p>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <small className="text-muted">
                                                {note.createdAt?.substring(0, 10)}
                                            </small>
                                            {!readOnly && canDelete(note.createdBy) && (
                                                <div className="d-flex gap-2">
                                                    <Button
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        onClick={() => handleStartEdit(note)}
                                                        disabled={submitting}
                                                    >
                                                        <Edit2 size={14} />
                                                    </Button>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => handleDeleteClick(note.key)}
                                                        disabled={submitting}
                                                    >
                                                        <Trash2 size={14} />
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </Card.Body>
            </Card>

            <ConfirmationModal
                show={deleteConfirm.show}
                title="Ta bort anteckning"
                message="Vill du ta bort denna anteckning?"
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                loading={submitting}
            />
        </>
    );
};

export default GuestNotesSection;
